/********************************************************************
 * Signature Creation Service module
 * Version 2017-08-18
 * Copyright 2016-2017 Vaestorekisterikeskus
 *
 * Permission is hereby granted, free of charge, to any person 
 * obtaining a copy of this software and associated documentation
 * files (the "Software"), to deal in the Software without
 * restriction, including without limitation the rights to use, 
 * copy, modify, merge, publish, distribute, sublicense, and/or 
 * sell copies of the Software, and to permit persons to whom the 
 * Software is furnished to do so, subject to the following 
 * conditions:
 *
 * The above copyright notice and this permission notice shall be 
 * included in all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, 
 * EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES
 * OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND 
 * NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT
 * HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, 
 * WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR 
 * OTHER DEALINGS IN THE SOFTWARE.
 *
 *********************************************************************/

//note: this file is included also in identityserver so it must work without transpiliting and bundling

//safari 15 does not support AbortSignal.timeout. browserslist etc should probably take vare of this, but it doesn't seem to work. so let's polyfill it. 
//polyfill from https://stackoverflow.com/a/50101022
AbortSignal.timeout ??= function timeout(ms) {
  const ctrl = new AbortController()
  setTimeout(() => ctrl.abort(), ms)
  return ctrl.signal
}

export type ScsResponse = { status: string, signature?: Blob,  reasonCode: number, reasonText: string };
export type ScsCallback = (response: ScsResponse) => void;

const SCS = (function () {

  /************************************************************/
  /**** SCS module private methods ****************************/
  /************************************************************/

  // setup initial parameters
  let secure_urls = [
    "https://localhost:53952",
    "https://127.0.0.1:53952"
  ];
  let plain_urls = [
    "http://localhost:53951",
    "http://127.0.0.1:53951"
  ];

  const secure_localhost = ["https://localhost:53952"];
  const plain_localhost = ["http://localhost:53951"];

  let urls :string[] | null= null ;
  let url :string|null = null;
  const available_urls :string[] = [];
  let initialized = false;
  let last_autodiscovery_check = 0;
  let version_info = new Object();
  // detect which port is in use

  checkbrowser();

  console.log("SCS: initiating automatic service discovery...");
  autodiscovery(0);

  /**
   * Get last time check autodiscovery was executed
   */
  function getHowMuchSinceLastAutodiscovery() {
    const now = new Date().getTime();
    return now - getLastAutodiscoveryTime();
  }

	/**
	 * Gets the last autodisovery time.
	 */
  function getLastAutodiscoveryTime():number {
    try {
      const lastAutodiscoveryTime = localStorage.getItem("scs_last_autodiscovery_time");
      return lastAutodiscoveryTime ? parseInt(lastAutodiscoveryTime) : last_autodiscovery_check;
    } catch (ex) { 
      //intentionally empty
    }
    return last_autodiscovery_check;
  }

	/**
	 * Sets the last autodiscovery time.
	 */
  function setLastAutodiscoveryTime() {
    last_autodiscovery_check = new Date().getTime();
    try {
      localStorage.setItem("scs_last_autodiscovery_time", last_autodiscovery_check.toString());
    } catch (ex) { 
      //intentionally empty
    }
  }

	/**
	 * Gets SCS URL from localStorage. Returns null, if not found.
	 */
  function getScsUrl() {
    try {
      return localStorage.getItem("scsurl");
    } catch (ex) {
      //intentionally empty
    }
    return null;
  }

	/**
	 * Sets SCS URL to localStorage.
	 */
  function setScsUrl(scsUrl: string | null) {
    try {
      if (scsUrl != null)
        localStorage.setItem("scsurl", scsUrl);
      else
        localStorage.removeItem("scsurl");
    } catch (ex) {
      //intentionally empty
    }
  }

  function pingScsUrl(scsUrl: string) {
    const url_version = scsUrl.substr(0, scsUrl.indexOf("/sign")) + "/version";
    console.log("SCS: ping scs url: " + url_version);

    fetch(url_version, {
      cache: "no-cache",
      method: "GET",
      mode: "cors",
      //atostek version request takes at least 6 seconds based on local testing in Lauri's computer (2025-01-20)
      signal: AbortSignal.timeout(15000)
    }).then((response) => response.json()).then((data) => {
      console.log("SCS: ping successful");
      init(data);
    }).catch((err) => {
      if (err?.name === "TimeoutError")
        console.log("SCS: ping timeouted");
      else
        console.log("SCS: ping failed");
    });
  }

  function isEDGE() {
    return (navigator.userAgent.indexOf("Trident/") > 0);
  }

	/**
	 * Checking browser and doing necessary adjustments.
	 */
  function checkbrowser() {
    if (isEDGE()) {
      // We use only localhost with IE11/Edge as 127.0.0.1 will cause an error dialog in IE11
      console.log("SCS: Is Edge brwoser, using only localhost");
      secure_urls = secure_localhost;
      plain_urls = plain_localhost;
    }
  }

	/**
 	 * Autodiscovery of the SCS URL on localhost from defined port list.
	 */
  function autodiscovery(i : number) {
    /* Let's check if SCS URL has been stored. */
    url = getScsUrl();
    if (url != null) {
      console.log("SCS: found SCS URL from localStorage: " + url);
      pingScsUrl(url);
      return;
    }

    /* We need to uncomment this if we cannot implement SCS normally on iOS
    if (isIOS()) {
       //port = "ios";
       console.log("SCS: SCS service is running on iOS");
       initialized = true;
       return;
    }
    */

    /* 
       We need to check whether the document was retrieved
       over http or https as CORS can typically be only be
       done to over https if the document was retrieved over
       https.
    */
    if (i == 0) {
      setLastAutodiscoveryTime();
      if (window.location.protocol == "https:") {
        urls = secure_urls;
      } else {
        urls = plain_urls;
      }
    }
    if(urls == null) throw new Error("urls is null, this should not happen");
    if (i >= urls.length) {
      console.log("SCS: All discovery urls probed, waiting for service discovery...");
      return;
    }

    const url_version = urls[i] + "/version";
    fetch(url_version, {
      cache: "no-cache",
      method: "GET",
      mode: "cors",
      //atostek version request takes at least 6 seconds based on local testing in Lauri's computer (2025-01-20)
      //let's set timeout to 15 seconds
      signal: AbortSignal.timeout(15000)
    }).then((response) => response.json()).then((data) => {
      if(urls == null) throw new Error("urls is null, this should not happen");
      url = urls[i] + "/sign";
      setScsUrl(url);
      available_urls.push(url);
      initialized = true;
      init(data);
      console.log("SCS: found SCS service at " + url);
    }).catch((err) => {
      if (err?.name === "TimeoutError")
        console.log("SCS: service call timeouted");
      else {
        console.log("SCS: service call failed");
        if (err instanceof Object && err.status == 200) {
          // We are because SCS implementation does not support 
          // version check (like early versions of Fujitsu DigiSign).
          // If status is 200 then there was a server responding,
          // but we are not 100% sure that it is SCS service.
          if(urls == null) throw new Error("urls is null, this should not happen");
          url = urls[i] + "/sign";
          setScsUrl(url);
          available_urls.push(url);
          console.log("SCS: " + url);
          console.log("SCS: Found a service in " + url + ", but we are not 100% sure it is SCS service. We use this port if no valid SCS service is found.");
        }
        else {
          console.log(err);
        }
      }
    });

    autodiscovery(i + 1);
  }

	/**
	 * Initialize the SCS service parameters.
	 */
  function init(response:any) {
    // store possible version parameters of SCS service, i.e., is
    // selector supported, SCS version, supported HTTP methods, etc.
    console.log("init!");
    console.log(response);
    version_info = response;
  }

	/**
	 * Check if we are on iOS device.
	 */
  function isIOS() {
    try {
      return (navigator.userAgent.match(/(iPad|iPhone|iPod)/g) ? true : false);
    } catch (ex) {
      return false;
    }
  }

	/**
	 * Do SCS signing request based on request parameters.
	 */
  function sign_localhost(callback: ScsCallback, request:any) {
    let diff = new Date().getTime();
    if(url == null) throw new Error("url is null, this should not happen");
    
    fetch(url, {
      body: JSON.stringify(request),
      cache: "no-cache",
      headers: { "Content-Type": "application/json" },
      method: "POST",
      mode: "cors",
      signal: AbortSignal.timeout(200000)
    }).then((response) => response.json()).then((data) => {
      doCallback(callback, data);
    }).catch((err) => {
      if (err?.name === "TimeoutError")
        console.log("SCS: sign callback timeouted");
      else {
        console.log("SCS: sign callback failed");
        diff = new Date().getTime() - diff;
        if (diff < 10000) {
          console.log("SCS: SCS is not responding");
          setScsUrl(null);
          url = null;
          const response: ScsResponse = { status: 'failed', reasonCode: 500, reasonText: "SCS not responding" };
          doCallback(callback, response);
        } else {
          console.log("SCS: local timeout in " + diff + "ms");
          const response : ScsResponse ={ status: 'failed', reasonCode: 401, reasonText: "Unauthorized: local timeout"};
          doCallback(callback, response);
        }
      }
    });
  }

	/**
	 * Do SCS signing in iOS (it maybe that SCS as such is not possible in iOS)
	 */
  function sign_ios(callback:ScsCallback, request:any) {

    //  We need to implement this part if SCS cannot be implemented on iOS

    /*  This is for the "not implemented notification
    var response = new Object();
    response.ok=false,
    response.reasonCode=500,
    response.reasonText="iOS not supported"
    setTimeout(function() { doCallback(callback,response); }, 10);
    */

    // But for now, we use normal SCS localhost signing
    sign_localhost(callback, request);

  }

	/**
	 * Check if SCS service is available.
         */
  function isAvailable() {
    return (url != null);
  }

	/**
	 * Check if SCS module is initialized (i.e., is finished with autodiscovery of the port).
	 */
  function isInitialized() {
    return initialized;
  }

	/**
	 * Single point to callback application code.
	 */
  function doCallback(callback: ScsCallback, response: ScsResponse) {
    try {
      response = validate(response);
      callback(response);
    } catch (ex) {
      // This exception should've been catched on caller side!
      console.log(ex);
    }
  }

  function validate(response: ScsResponse) {
    // here we can do a sanity check for the response
    return response;
  }

  interface IScsRequest {    
    selector?: {
      keyusages?: string[];
    };
    content?: string;
    contentType?: string;
    hashAlgorithm?: string;
    signatureType?: string;
    version?: string;
  }
  
  function doSign(callback:ScsCallback, request:IScsRequest) {
    if (!isAvailable()) {
      if (getHowMuchSinceLastAutodiscovery() > 6000) {
        console.log("SCS: doing autodiscovery, retrying signature generation in one second...");
        autodiscovery(0);
        setTimeout(function () {
          doSign(callback, request);
        }, 1000);
      } else {
        console.log("SCS: SCS is not available");
        const response : ScsResponse = { status: 'failed', reasonCode: 500, reasonText: "SCS not available" };
        setTimeout(function () { doCallback(callback, response); }, 10);
      }
      return;
    }

    if (isIOS()) {
      console.log("SCS: Doing signature with ios...");
      sign_ios(callback, request);
    } else {
      console.log("SCS: Doing signature with SCS service...");
      sign_localhost(callback, request);
    }

  }

  /************************************************************/
  /**** SCS module methods ************************************/
  /************************************************************/

  const my = {
    /**
           * Check is SCS module is ready.
     */
    isAvailable() {
      return isAvailable();
    },

    /**
     * Request signature from SCS.
     */
    sign: doSign,

    /**
           * Set SCS port manually (e.g., in the case if port autodiscovery fails).
     */
    setURL(newurl : string|null) : void {
      setScsUrl(newurl);
      url = newurl;
    },

    getURL() {
      return url;
    },

    getAvailableURLs() {
      return available_urls;
    },

    getVersionInfo() {
      return version_info;
    }
  }

  return my;

}());
export default SCS;
